import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useLanguageContext } from '../contexts/LanguageContext';
import { Capacitor } from '@capacitor/core';

interface LegalPageScreenProps {
  slug: 'terms' | 'privacy';
  onBack: () => void;
}

interface LegalPage {
  id: number;
  slug: string;
  lang: string;
  title: string;
  content: string;
  version: string;
  last_updated_at: string;
}

const LegalPageScreen: React.FC<LegalPageScreenProps> = ({ slug, onBack }) => {
  const { language } = useLanguageContext();
  const [page, setPage] = useState<LegalPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    loadPage();
  }, [slug, language]);

  const loadPage = async () => {
    setLoading(true);
    setError(null);

    try {
      // Try to get page in current language
      let { data, error: err } = await supabase
        .from('legal_pages')
        .select('*')
        .eq('slug', slug)
        .eq('lang', language)
        .single();

      // Fallback to French if not found
      if (err || !data) {
        const fallback = await supabase
          .from('legal_pages')
          .select('*')
          .eq('slug', slug)
          .eq('lang', 'fr')
          .single();

        if (fallback.error) throw fallback.error;
        data = fallback.data;
      }

      setPage(data);
    } catch (err: any) {
      console.error('Error loading legal page:', err);
      setError('Impossible de charger cette page');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`h-screen-adaptive bg-gray-50 flex items-center justify-center ${isNative ? 'safe-area-top safe-area-bottom' : ''}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className={`h-screen-adaptive bg-gray-50 ${isNative ? 'safe-area-top safe-area-bottom' : ''}`}>
        <div className="bg-white shadow-sm">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ChevronLeft size={24} className="text-gray-700" />
            </button>
            <h1 className="text-xl font-bold text-gray-800">Erreur</h1>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          <p className="text-red-600">{error || 'Page non trouvée'}</p>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen-adaptive bg-gray-50 ${isNative ? 'safe-area-top safe-area-bottom' : ''}`}>
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-800 truncate">{page.title}</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          {/* Version and date */}
          <div className="text-sm text-gray-500 mb-6 pb-4 border-b border-gray-100">
            Version {page.version} • Dernière mise à jour : {new Date(page.last_updated_at).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>

          {/* HTML Content */}
          <div
            className="prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-li:text-gray-600 prose-strong:text-gray-700"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </div>
      </div>
    </div>
  );
};

export default LegalPageScreen;
